import { c as _c } from "react/compiler-runtime";
import React, { useContext, useEffect, useState } from "react";
import styles from "./TaxDeductionWidget.module.scss";
import { TaxDeductionBarChart } from "./TaxDeductionBarChart";
import { EffektButton, EffektButtonVariant } from "../../../shared/components/EffektButton/EffektButton";
import { EffektTextInput } from "../../../shared/components/EffektTextInput/EffektTextInput";
import { WidgetContext } from "../../layout/layout";
import { HorizontalTaxDeductionBarChart } from "./TaxDeductionBarChartHorizontal";
import { useIsMobile } from "../../../../hooks/useIsMobile";
import { usePlausible } from "next-plausible";
import { thousandize } from "../../../../util/formatting";
export const TaxDeductionWidget: React.FC<{
  title: string;
  description: string;
  suggestedSums: number[];
  minimumTreshold: number;
  maximumTreshold: number;
  percentageReduction: number;
  donationsLabel?: string;
  taxDeductionReturnDescriptionTemplate?: string;
  belowMinimumTresholdDescriptionTemplate?: string;
  buttonText?: string;
  chartLabels?: {
    maximumThresholdLabel?: string;
    minimumThresholdLabel?: string;
    currentValueLabel?: string;
    taxBenefitLabel?: string;
  };
  locale: string;
}> = t0 => {
  const $ = _c(88);
  const {
    title,
    description,
    suggestedSums,
    minimumTreshold,
    maximumTreshold,
    percentageReduction,
    donationsLabel,
    taxDeductionReturnDescriptionTemplate,
    belowMinimumTresholdDescriptionTemplate,
    buttonText,
    chartLabels,
    locale
  } = t0;
  const plausible = usePlausible();
  let t1;
  if ($[0] !== suggestedSums) {
    t1 = suggestedSums.slice(-2)[0].toString();
    $[0] = suggestedSums;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [sum, setSum] = React.useState(t1);
  let t2;
  if ($[2] !== suggestedSums) {
    t2 = suggestedSums.slice(-2);
    $[2] = suggestedSums;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [lastValidSum, setLastValidSum] = useState(t2[0]);
  const [widgetContext, setWidgetContext] = useContext(WidgetContext);
  const isMobile = useIsMobile();
  let t3;
  if ($[4] !== locale || $[5] !== sum) {
    t3 = () => {
      if (sum === "") {
        setLastValidSum(0);
        return;
      }
      const sumNumber = Number(sum.replace(/\D/g, ""));
      if (!isNaN(sumNumber)) {
        setLastValidSum(sumNumber);
        const formattedSum = thousandize(sumNumber, locale);
        if (formattedSum !== sum) {
          setSum(formattedSum);
        }
      }
    };
    $[4] = locale;
    $[5] = sum;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== sum) {
    t4 = [sum];
    $[7] = sum;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  useEffect(t3, t4);
  const taxBenefit = lastValidSum >= minimumTreshold ? Math.min(lastValidSum, maximumTreshold) * percentageReduction : 0;
  const taxDeduction = Math.min(lastValidSum, maximumTreshold);
  let t5;
  if ($[9] !== chartLabels || $[10] !== isMobile || $[11] !== lastValidSum || $[12] !== locale || $[13] !== maximumTreshold || $[14] !== minimumTreshold || $[15] !== taxBenefit) {
    t5 = !isMobile && <TaxDeductionBarChart maximumThreshold={maximumTreshold} minimumThreshold={minimumTreshold} value={lastValidSum} taxBenefit={taxBenefit} labels={chartLabels} locale={locale} />;
    $[9] = chartLabels;
    $[10] = isMobile;
    $[11] = lastValidSum;
    $[12] = locale;
    $[13] = maximumTreshold;
    $[14] = minimumTreshold;
    $[15] = taxBenefit;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== t5) {
    t6 = <div className={styles.desktopChart}>{t5}</div>;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== title) {
    t7 = <h5>{title}</h5>;
    $[19] = title;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== description) {
    t8 = <p>{description}</p>;
    $[21] = description;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== chartLabels || $[24] !== isMobile || $[25] !== lastValidSum || $[26] !== locale || $[27] !== maximumTreshold || $[28] !== minimumTreshold || $[29] !== taxBenefit) {
    t9 = isMobile && <HorizontalTaxDeductionBarChart maximumThreshold={maximumTreshold} minimumThreshold={minimumTreshold} value={lastValidSum} taxBenefit={taxBenefit} labels={chartLabels} locale={locale} />;
    $[23] = chartLabels;
    $[24] = isMobile;
    $[25] = lastValidSum;
    $[26] = locale;
    $[27] = maximumTreshold;
    $[28] = minimumTreshold;
    $[29] = taxBenefit;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== t9) {
    t10 = <div className={styles.mobileChart}>{t9}</div>;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== lastValidSum || $[34] !== locale || $[35] !== plausible || $[36] !== suggestedSums) {
    let t12;
    if ($[38] !== lastValidSum || $[39] !== locale || $[40] !== plausible) {
      t12 = suggestedSum => {
        const formattedSuggestedSum = thousandize(suggestedSum, locale);
        return <EffektButton key={suggestedSum} onClick={() => {
          plausible("TaxWidgetSelectSuggestedSum", {
            props: {
              page: window.location.pathname,
              sum: suggestedSum
            }
          });
          setSum(formattedSuggestedSum);
        }} variant={EffektButtonVariant.SECONDARY} selected={lastValidSum === suggestedSum} style={{
          fontSize: "0.9rem"
        }} squared={true}>{formattedSuggestedSum} kr</EffektButton>;
      };
      $[38] = lastValidSum;
      $[39] = locale;
      $[40] = plausible;
      $[41] = t12;
    } else {
      t12 = $[41];
    }
    t11 = suggestedSums.map(t12);
    $[33] = lastValidSum;
    $[34] = locale;
    $[35] = plausible;
    $[36] = suggestedSums;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[42] !== t11) {
    t12 = <div className={styles.suggestedSums}>{t11}</div>;
    $[42] = t11;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  const t13 = donationsLabel ?? "Donasjoner";
  let t14;
  if ($[44] !== t13) {
    t14 = <label htmlFor="">{t13}:</label>;
    $[44] = t13;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  let t15;
  if ($[46] !== sum) {
    t15 = sum.toString();
    $[46] = sum;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = val => {
      setSum(val);
    };
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  let t17;
  if ($[49] !== t15) {
    t17 = <EffektTextInput value={t15} name="taxDeductionSumInput" type="tel" placeholder="0" onChange={t16} denomination="kr" variant="underlined" />;
    $[49] = t15;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  let t18;
  if ($[51] !== t14 || $[52] !== t17) {
    t18 = <div className={styles.input}>{t14}{t17}</div>;
    $[51] = t14;
    $[52] = t17;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] !== t12 || $[55] !== t18) {
    t19 = <div className={styles.selectSum}>{t12}{t18}</div>;
    $[54] = t12;
    $[55] = t18;
    $[56] = t19;
  } else {
    t19 = $[56];
  }
  let t20;
  if ($[57] !== locale || $[58] !== taxBenefit || $[59] !== taxDeduction || $[60] !== taxDeductionReturnDescriptionTemplate) {
    t20 = taxBenefit > 0 && <p>{taxDeductionReturnDescriptionTemplate ? taxDeductionReturnDescriptionTemplate.replace("{taxDeduction}", thousandize(taxDeduction, locale)).replace("{taxBenefit}", thousandize(taxBenefit, locale)) : `Med ${Intl.NumberFormat("no-NB").format(taxDeduction)} kr i skattefradrag i år får du tilbake ${Intl.NumberFormat("no-NB").format(taxBenefit)} kroner på skatten.`}</p>;
    $[57] = locale;
    $[58] = taxBenefit;
    $[59] = taxDeduction;
    $[60] = taxDeductionReturnDescriptionTemplate;
    $[61] = t20;
  } else {
    t20 = $[61];
  }
  let t21;
  if ($[62] !== belowMinimumTresholdDescriptionTemplate || $[63] !== lastValidSum || $[64] !== locale || $[65] !== minimumTreshold || $[66] !== taxBenefit) {
    t21 = taxBenefit === 0 && <p>{belowMinimumTresholdDescriptionTemplate ? belowMinimumTresholdDescriptionTemplate.replace("{lastValidSum}", thousandize(lastValidSum, locale)).replace("{minimumTreshold}", thousandize(minimumTreshold, locale)) : `Med ${Intl.NumberFormat("no-NB").format(lastValidSum)} kr i donasjoner er du under minstegrensen på ${Intl.NumberFormat("no-NB").format(minimumTreshold)} kr for å få skattefradrag.`}</p>;
    $[62] = belowMinimumTresholdDescriptionTemplate;
    $[63] = lastValidSum;
    $[64] = locale;
    $[65] = minimumTreshold;
    $[66] = taxBenefit;
    $[67] = t21;
  } else {
    t21 = $[67];
  }
  let t22;
  if ($[68] !== lastValidSum || $[69] !== plausible || $[70] !== setWidgetContext || $[71] !== widgetContext) {
    t22 = () => {
      plausible("OpenDonationWidget", {
        props: {
          page: window.location.pathname
        }
      });
      plausible("OpenDonationWidgetTaxWidgetCTA", {
        props: {
          page: window.location.pathname,
          sum: lastValidSum
        }
      });
      setWidgetContext({
        ...widgetContext,
        open: true,
        prefilledSum: lastValidSum
      });
    };
    $[68] = lastValidSum;
    $[69] = plausible;
    $[70] = setWidgetContext;
    $[71] = widgetContext;
    $[72] = t22;
  } else {
    t22 = $[72];
  }
  let t23;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = {
      padding: "0.75rem 3rem"
    };
    $[73] = t23;
  } else {
    t23 = $[73];
  }
  const t24 = buttonText ?? "F\xE5 skattefradrag";
  let t25;
  if ($[74] !== t22 || $[75] !== t24) {
    t25 = <div className={styles.cta}><EffektButton onClick={t22} variant={EffektButtonVariant.PRIMARY} style={t23}>{t24}</EffektButton></div>;
    $[74] = t22;
    $[75] = t24;
    $[76] = t25;
  } else {
    t25 = $[76];
  }
  let t26;
  if ($[77] !== t10 || $[78] !== t19 || $[79] !== t20 || $[80] !== t21 || $[81] !== t25 || $[82] !== t7 || $[83] !== t8) {
    t26 = <div className={styles.description}>{t7}{t8}{t10}{t19}{t20}{t21}{t25}</div>;
    $[77] = t10;
    $[78] = t19;
    $[79] = t20;
    $[80] = t21;
    $[81] = t25;
    $[82] = t7;
    $[83] = t8;
    $[84] = t26;
  } else {
    t26 = $[84];
  }
  let t27;
  if ($[85] !== t26 || $[86] !== t6) {
    t27 = <div className={styles.wrapper}><div className={styles.container}>{t6}{t26}</div></div>;
    $[85] = t26;
    $[86] = t6;
    $[87] = t27;
  } else {
    t27 = $[87];
  }
  return t27;
};